import { useEffect, useState, type FormEvent } from "react";
import { api, ApiError } from "../../api/client";
import type { BaseExercise, Exercise, ExerciseCategory, ExerciseParam, ImportBaseResult, MuscleGroup, Page } from "../../api/types";
import { Badge, Button, EmptyState, ErrorState, FormError, Loading, Modal, PageHead, Pagination, SelectField, TextArea, TextField, useToast } from "../../components/ui";
import { CATEGORIES, CATEGORY_LABEL, MAX_MUSCLES, MUSCLES, MUSCLE_LABEL, PARAMS, PARAM_LABEL } from "../../lib/strength";
import { useAsync } from "../../lib/useAsync";
import { t, tn } from "../../i18n";

const PAGE_SIZE = 20;

interface FormState {
  name: string;
  description: string;
  category: ExerciseCategory;
  muscle_groups: MuscleGroup[];
  equipment: string;
  param_type: ExerciseParam;
  notes: string;
}

function toForm(e: Exercise | null): FormState {
  return {
    name: e?.name ?? "",
    description: e?.description ?? "",
    category: e?.category ?? "strength",
    muscle_groups: e?.muscle_groups ?? [],
    equipment: e?.equipment ?? "",
    param_type: e?.param_type ?? "reps_load",
    notes: e?.notes ?? "",
  };
}

function ExerciseForm({ initial, onCancel, onSubmit }: { initial: Exercise | null; onCancel: () => void; onSubmit: (v: FormState) => Promise<void> }) {
  const [v, setV] = useState<FormState>(() => toForm(initial));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const fields = error?.fieldErrors() ?? {};
  const set = <K extends keyof FormState>(k: K, val: FormState[K]) => setV((s) => ({ ...s, [k]: val }));

  function toggleMuscle(m: MuscleGroup) {
    setV((s) => {
      const has = s.muscle_groups.includes(m);
      if (!has && s.muscle_groups.length >= MAX_MUSCLES) return s;
      return { ...s, muscle_groups: has ? s.muscle_groups.filter((x) => x !== m) : [...s.muscle_groups, m] };
    });
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await onSubmit(v);
    } catch (err) {
      setError(err instanceof ApiError ? err : new ApiError(0, "unknown", String(err)));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="form" onSubmit={submit} noValidate>
      <FormError error={error} />
      <TextField label={t("Nome do exercício")} value={v.name} maxLength={120} required autoFocus error={fields.name} onChange={(e) => set("name", e.target.value)} />
      <div className="form-grid">
        <SelectField label={t("Categoria")} value={v.category} onChange={(e) => set("category", e.target.value as ExerciseCategory)}>
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>{CATEGORY_LABEL[c]}</option>
          ))}
        </SelectField>
        <SelectField
          label={t("Como é prescrito")}
          hint={t("Define o que cada série precisa informar.")}
          value={v.param_type}
          onChange={(e) => set("param_type", e.target.value as ExerciseParam)}
        >
          {PARAMS.map((p) => (
            <option key={p} value={p}>{PARAM_LABEL[p]}</option>
          ))}
        </SelectField>
      </div>
      <fieldset className="chip-group">
        <legend>{t("Grupos musculares (até {n})", { n: MAX_MUSCLES })}</legend>
        <div className="chip-list">
          {MUSCLES.map((m) => {
            const on = v.muscle_groups.includes(m);
            return (
              <label key={m} className={`chip${on ? " on" : ""}`}>
                <input type="checkbox" checked={on} disabled={!on && v.muscle_groups.length >= MAX_MUSCLES} onChange={() => toggleMuscle(m)} />
                {MUSCLE_LABEL[m]}
              </label>
            );
          })}
        </div>
        {fields.muscle_groups && <span className="error" role="alert">{fields.muscle_groups}</span>}
      </fieldset>
      <TextField label={t("Equipamento")} value={v.equipment} maxLength={80} error={fields.equipment} onChange={(e) => set("equipment", e.target.value)} />
      <TextArea label={t("Como executar")} rows={4} value={v.description} maxLength={4000} error={fields.description} onChange={(e) => set("description", e.target.value)} />
      <TextArea
        label={t("Observações gerais")}
        hint={t("Cuidados e adaptações definidos por você. O sistema não faz recomendações médicas.")}
        rows={3}
        value={v.notes}
        maxLength={2000}
        error={fields.notes}
        onChange={(e) => set("notes", e.target.value)}
      />
      <div className="form-actions">
        <Button onClick={onCancel}>{t("Cancelar")}</Button>
        <Button type="submit" variant="primary" busy={busy}>{initial ? t("Salvar exercício") : t("Criar exercício")}</Button>
      </div>
    </form>
  );
}

function BaseSetDialog({ orgId, onClose, onDone }: { orgId: string; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const base = useAsync(() => api.get<BaseExercise[]>(`/orgs/${orgId}/exercises/base-set`), [orgId]);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const pending = (base.data ?? []).filter((b) => !b.imported);

  function toggle(key: string) {
    setPicked((s) => {
      const n = new Set(s);
      if (n.has(key)) n.delete(key);
      else n.add(key);
      return n;
    });
  }

  async function run(keys: string[] | null) {
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<ImportBaseResult>(`/orgs/${orgId}/exercises/base-set/import`, keys ? { keys } : {});
      const msg = tn("{n} exercício importado.", "{n} exercícios importados.", r.created, { n: r.created });
      toast(r.name_conflicts.length ? `${msg} ${t("Não importados por já existir um exercício com o mesmo nome: {names}.", { names: r.name_conflicts.join(", ") })}` : msg);
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err : new ApiError(0, "unknown", String(err)));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <p className="muted">{t("Exercícios comuns para você começar. Eles são copiados para a sua biblioteca e você pode editá-los. Nada é sobrescrito e o que você já importou não é duplicado.")}</p>
      <FormError error={error} />
      {base.loading && !base.data && <Loading />}
      {base.error && <ErrorState error={base.error} onRetry={base.reload} />}
      {base.data && (
        <>
          <ul className="base-list">
            {base.data.map((b) => (
              <li key={b.key}>
                <label className="checkbox">
                  <input type="checkbox" disabled={b.imported} checked={b.imported || picked.has(b.key)} onChange={() => toggle(b.key)} />
                  <span>
                    <strong>{b.name}</strong> {b.imported && <Badge tone="ok">{t("Já importado")}</Badge>}
                    <span className="sub">{[CATEGORY_LABEL[b.category], b.muscle_groups.map((m) => MUSCLE_LABEL[m]).join(", "), b.equipment].filter(Boolean).join(" · ")}</span>
                  </span>
                </label>
              </li>
            ))}
          </ul>
          <div className="form-actions">
            <Button onClick={onClose}>{t("Fechar")}</Button>
            <Button busy={busy} disabled={picked.size === 0} onClick={() => run([...picked])}>
              {t("Importar selecionados ({n})", { n: picked.size })}
            </Button>
            <Button variant="primary" busy={busy} disabled={pending.length === 0} onClick={() => run(null)}>
              {t("Importar todos os que faltam")}
            </Button>
          </div>
        </>
      )}
    </>
  );
}

export function ExercisesPage({ orgId }: { orgId: string }) {
  const toast = useToast();
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [category, setCategory] = useState("");
  const [muscle, setMuscle] = useState("");
  const [param, setParam] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<Exercise | "new" | null>(null);
  const [baseOpen, setBaseOpen] = useState(false);

  useEffect(() => {
    const tp = setTimeout(() => {
      setDebounced(q);
      setPage(1);
    }, 300);
    return () => clearTimeout(tp);
  }, [q]);

  const list = useAsync(
    () =>
      api.get<Page<Exercise>>(`/orgs/${orgId}/exercises`, {
        q: debounced || undefined,
        category: category || undefined,
        muscle: muscle || undefined,
        param_type: param || undefined,
        include_inactive: showArchived,
        page,
        page_size: PAGE_SIZE,
      }),
    [orgId, debounced, category, muscle, param, showArchived, page],
  );

  const filtered = !!(debounced || category || muscle || param);

  async function toggle(e: Exercise) {
    try {
      await api.post(`/orgs/${orgId}/exercises/${e.id}/${e.is_active ? "archive" : "restore"}`);
      toast(e.is_active ? t("Exercício “{name}” arquivado.", { name: e.name }) : t("Exercício “{name}” reativado.", { name: e.name }));
      list.reload();
    } catch (err) {
      toast((err as ApiError).message, "error");
    }
  }

  const create = <Button variant="primary" onClick={() => setEditing("new")}>{t("Criar exercício")}</Button>;

  return (
    <>
      <PageHead
        title={t("Exercícios de força")}
        subtitle={t("Biblioteca da sua assessoria. Treinos já prescritos guardam uma cópia do nome, então arquivar ou renomear um exercício não altera o histórico.")}
        actions={
          <>
            <Button onClick={() => setBaseOpen(true)}>{t("Importar conjunto-base")}</Button>
            {create}
          </>
        }
      />
      <div className="toolbar" role="search">
        <div className="grow">
          <TextField label={t("Buscar")} type="search" placeholder={t("Nome do exercício")} value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <SelectField label={t("Categoria")} value={category} onChange={(e) => { setCategory(e.target.value); setPage(1); }}>
          <option value="">{t("Todas")}</option>
          {CATEGORIES.map((c) => <option key={c} value={c}>{CATEGORY_LABEL[c]}</option>)}
        </SelectField>
        <SelectField label={t("Grupo muscular")} value={muscle} onChange={(e) => { setMuscle(e.target.value); setPage(1); }}>
          <option value="">{t("Todos")}</option>
          {MUSCLES.map((m) => <option key={m} value={m}>{MUSCLE_LABEL[m]}</option>)}
        </SelectField>
        <SelectField label={t("Prescrição")} value={param} onChange={(e) => { setParam(e.target.value); setPage(1); }}>
          <option value="">{t("Todas")}</option>
          {PARAMS.map((p) => <option key={p} value={p}>{PARAM_LABEL[p]}</option>)}
        </SelectField>
      </div>
      <label className="checkbox" style={{ marginBottom: 12 }}>
        <input type="checkbox" checked={showArchived} onChange={(e) => { setShowArchived(e.target.checked); setPage(1); }} />
        {t("Mostrar arquivados")}
      </label>

      {list.loading && !list.data && <Loading />}
      {list.error && <ErrorState error={list.error} onRetry={list.reload} />}
      {list.data && list.data.total === 0 && (
        <div className="panel">
          <EmptyState
            title={filtered ? t("Nenhum exercício encontrado") : t("Nenhum exercício ainda")}
            action={filtered ? undefined : <>{create} <Button onClick={() => setBaseOpen(true)}>{t("Importar conjunto-base")}</Button></>}
          >
            {filtered ? t("Tente outros filtros ou limpe a busca.") : t("Cadastre os exercícios que você usa ou importe o conjunto-base para começar mais rápido.")}
          </EmptyState>
        </div>
      )}
      {list.data && list.data.total > 0 && (
        <>
          <div className="table-wrap">
            <table className="data responsive">
              <thead>
                <tr>
                  <th>{t("Exercício")}</th>
                  <th>{t("Categoria")}</th>
                  <th>{t("Grupos musculares")}</th>
                  <th>{t("Prescrição")}</th>
                  <th className="right">{t("Ações")}</th>
                </tr>
              </thead>
              <tbody>
                {list.data.items.map((e) => (
                  <tr key={e.id}>
                    <td>
                      <strong>{e.name}</strong> {!e.is_active && <Badge>{t("Arquivado")}</Badge>}
                      {e.equipment && <span className="sub">{e.equipment}</span>}
                    </td>
                    <td data-label={t("Categoria")}>{CATEGORY_LABEL[e.category]}</td>
                    <td data-label={t("Grupos musculares")}>{e.muscle_groups.map((m) => MUSCLE_LABEL[m]).join(", ") || "—"}</td>
                    <td data-label={t("Prescrição")}>{PARAM_LABEL[e.param_type]}</td>
                    <td className="right" style={{ whiteSpace: "nowrap" }}>
                      <Button size="sm" variant="ghost" onClick={() => setEditing(e)}>{t("Editar")}</Button>
                      <Button size="sm" variant={e.is_active ? "danger" : "secondary"} onClick={() => toggle(e)}>
                        {e.is_active ? t("Arquivar") : t("Reativar")}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={page} pageSize={PAGE_SIZE} total={list.data.total} onPage={setPage} />
        </>
      )}

      <Modal open={editing !== null} title={editing === "new" ? t("Novo exercício") : t("Editar exercício")} onClose={() => setEditing(null)} wide>
        {editing !== null && (
          <ExerciseForm
            key={editing === "new" ? "new" : editing.id}
            initial={editing === "new" ? null : editing}
            onCancel={() => setEditing(null)}
            onSubmit={async (v) => {
              const body = { ...v, equipment: v.equipment.trim() || null, description: v.description.trim() || null, notes: v.notes.trim() || null };
              if (editing === "new") await api.post(`/orgs/${orgId}/exercises`, body);
              else await api.patch(`/orgs/${orgId}/exercises/${editing.id}`, body);
              toast(editing === "new" ? t("Exercício criado.") : t("Exercício salvo."));
              setEditing(null);
              list.reload();
            }}
          />
        )}
      </Modal>
      <Modal open={baseOpen} title={t("Conjunto-base de exercícios")} onClose={() => setBaseOpen(false)} wide>
        {baseOpen && <BaseSetDialog orgId={orgId} onClose={() => setBaseOpen(false)} onDone={() => { setBaseOpen(false); setPage(1); list.reload(); }} />}
      </Modal>
    </>
  );
}
